import React, { useState, useEffect, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import TextFieldGroup from '../common/textFieldGroup';
import SelectFieldGroup from '../common/selectFieldGroup';
import Button from 'react-bootstrap/Button';
import { connect } from 'react-redux';
import { addSubject } from '../../actions/subjectActions';
import validateAddSubject from '../../validations/addSubjectValidation';
import { withRouter } from '../../common/utils';
import States from '../../static/states.json';
import SelectFieldGroupFromArray from '../common/selectFieldGroupFromArray';
import Counties from '../../static/counties.json';
import ToastService from '../Toasts/toastService';
import Loader from '../Spinners/spinnerPage';

const AddressTextFieldGroup = ({ error, label, onChange, value, field, required, onAddressSelect }) => {
  const autocompleteInput = useRef(null);
  const autocomplete = useRef(null);
  useEffect(() => {
    loadGoogleMapsScript();
  }, []);

  const loadGoogleMapsScript = () => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  };

  const initAutocomplete = () => {
    if (autocompleteInput.current) {
      autocomplete.current = new window.google.maps.places.Autocomplete(
        autocompleteInput.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );
      autocomplete.current.addListener('place_changed', handlePlaceChanged);
    }
  };

  const handlePlaceChanged = () => {
    const place = autocomplete.current.getPlace();
    
    if (!place.geometry) {
      return;
    }
    
    let addressData = {
      address1: '',
      city: '',
      state: '',
      county: '',
      zip: ''
    };
    
    let streetNumber = '';
    let streetName = '';

    place.address_components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      
      if (types.includes('route')) {
        streetName = component.long_name;
      }
      
      if (types.includes('locality')) {
        addressData.city = component.long_name;
      }
      
      if (types.includes('administrative_area_level_1')) {
        addressData.state = component.short_name;
      }
      
      if (types.includes('administrative_area_level_2')) {
        addressData.county = component.long_name.replace(' County', '');
      }
      
      if (types.includes('postal_code')) {
        addressData.zip = component.long_name;
      }
    });
    
    addressData.address1 = `${streetNumber} ${streetName}`.trim();
    
    onChange({
      target: {
        name: 'address1',
        value: addressData.address1
      }
    });
    
    onAddressSelect(addressData);
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>{label}{required && <span className="text-danger">*</span>}</Form.Label>
      <Form.Control
        ref={autocompleteInput}
        type="text"
        placeholder="Enter your address..."
        name={field}
        value={value}
        onChange={onChange}
        isInvalid={!!error}
      />
      <Form.Text className="text-muted">
        As you type, address suggestions will appear. Select one to automatically fill city, state, county, and zip.
      </Form.Text>
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
};

const AddSubjectPage = ({ addSubject, addSubjectError, addSubjectSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    dob: '',
    ssn: '',
    gender: '',
    city: '',
    state: '',
    county: '',
    errors: {},
    counties: [],
    address1: '',
    address2: '',
    email: '',
    phone: '',
    zip: '',
    country: 'US'
  });

  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if (addSubjectError) {
      ToastService.show(
        'Individual has not been created, please contact support',
        'error'
      );
    }
    if (addSubjectSuccess) {
      ToastService.show("Individual added successfully", 'success');
      const timer = setTimeout(() => {
        window.location.href = '/individuals';
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [addSubjectError, addSubjectSuccess]);

  const onChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'state') {
      setFormData(prevState => ({
        ...prevState,
        counties: Counties[value.toString()],
        state: value
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleAddressSelect = (addressData) => {
    if (addressData.state && addressData.state !== formData.state) {
      setFormData(prevState => ({
        ...prevState,
        state: addressData.state,
        counties: Counties[addressData.state.toString()] || []
      }));
    }

    setFormData(prevState => ({
      ...prevState,
      city: addressData.city,
      zip: addressData.zip,
      county: addressData.county
    }));
  };

  const isValid = () => {
    const { errors, isValid } = validateAddSubject(formData);

    if (!isValid) {
      setFormData(prevState => ({
        ...prevState,
        errors
      }));
    }

    return isValid;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setShowLoader(true);
    
    if (isValid()) {
      addSubject({ ...formData, gender: formData.gender[0] });
    } else {
      setShowLoader(false);
    }
  };

  const loader = showLoader ? <Loader /> : null;
  
  return (
    <Container>
      <Row>
        <Col lg={12}>
          <Card className='card-box mt-5'>
            <Form onSubmit={onSubmit} onKeyDown={e => {if (e.key === 'Enter') e.preventDefault()}}>
              <Card.Body>
                <Card.Title>
                  <h2>
                    <i className='material-icons md-36'>person_add</i>Add
                    Individual
                  </h2>
                </Card.Title>
                <h5 className='mt-5 mb-3'>Individual's Personal Info</h5>
                <Card.Text />
                <TextFieldGroup
                  error={formData.errors.firstName}
                  label='First name'
                  onChange={onChange}
                  value={formData.firstName}
                  field='firstName'
                  required={true}
                />
                <TextFieldGroup
                  error={formData.errors.lastName}
                  label='Last name'
                  onChange={onChange}
                  value={formData.lastName}
                  field='lastName'
                  required={true}
                />
                <TextFieldGroup
                  error={formData.errors.middleName}
                  label='Middle name'
                  onChange={onChange}
                  value={formData.middleName}
                  field='middleName'
                />
                <TextFieldGroup
                  error={formData.errors.dob}
                  label='Date of Birth (Format: YYYY-MM-DD)'
                  onChange={onChange}
                  value={formData.dob}
                  field='dob'
                  required={true}
                />
                <TextFieldGroup
                  error={formData.errors.ssn}
                  label='SSN (Format: AAA-BB-CCCC)'
                  onChange={onChange}
                  value={formData.ssn}
                  field='ssn'
                />
                
                <AddressTextFieldGroup
                  error={formData.errors.address1}
                  label='Address 1'
                  onChange={onChange}
                  value={formData.address1}
                  field='address1'
                  onAddressSelect={handleAddressSelect}
                />
                
                <TextFieldGroup
                  error={formData.errors.address2}
                  label='Address 2'
                  onChange={onChange}
                  value={formData.address2}
                  field='address2'
                />
                <SelectFieldGroup
                  error={formData.errors.gender}
                  label='Gender'
                  onChange={onChange}
                  value={formData.gender}
                  required={true}
                  name='gender'
                />
                <TextFieldGroup
                  error={formData.errors.city}
                  label='City'
                  onChange={onChange}
                  value={formData.city}
                  field='city'
                />
                <SelectFieldGroupFromArray
                  error={formData.errors.state}
                  label='State'
                  onChange={onChange}
                  value={formData.state}
                  field='state'
                  options={States.Values}
                  name='state'
                  placeholder='- - Select state - -'
                  required={true}
                />
                <SelectFieldGroupFromArray
                  error={formData.errors.county}
                  label='County'
                  onChange={onChange}
                  value={formData.county}
                  options={formData.counties}
                  field='county'
                  name='county'
                  placeholder='- - Select county - -'
                  required={true}
                />
                <TextFieldGroup
                  error={formData.errors.zip}
                  label='Zip'
                  onChange={onChange}
                  value={formData.zip}
                  field='zip'
                />
                <TextFieldGroup
                  error={formData.errors.phone}
                  label='Phone'
                  onChange={onChange}
                  value={formData.phone}
                  field='phone'
                />
                <TextFieldGroup
                  error={formData.errors.email}
                  label='Email'
                  onChange={onChange}
                  value={formData.email}
                  field='email'
                />
                <Button variant='primary' className='mt-3 mb-2' type='submit' disabled={showLoader}>
                  <i className='material-icons'>person_add</i>
                  Add Individual
                </Button>
                {loader}
              </Card.Body>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapActionsToProps = {
  addSubject
};

const mapStateToProps = state => {
  return {
    addSubjectError: state.subjectReducer.addSubjectError,
    addSubjectSuccess: state.subjectReducer.subjectCreate
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapActionsToProps
  )(AddSubjectPage)
);